import { useState, useMemo } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import { Button, Card, Badge, cn } from '@pantry/ui';
import {
  useAnnouncements,
  type Announcement,
  type AnnouncementQueryParams,
  ANNOUNCEMENT_STATUSES,
  ANNOUNCEMENT_SEVERITIES,
  SEVERITY_LABELS,
  STATUS_LABELS,
  type AnnouncementStatus,
  type AnnouncementSeverity,
} from '@pantry/api-client';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  MegaphoneIcon,
  BellAlertIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { AnnouncementFormSheet } from './announcement-form-sheet';

// Status badge colors
const STATUS_BADGE_COLORS: Record<AnnouncementStatus, string> = {
  draft: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  scheduled: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  expired: 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400',
};

// Severity badge colors
const SEVERITY_BADGE_COLORS: Record<AnnouncementSeverity, string> = {
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  warning: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

// Severity icons
const SEVERITY_ICONS: Record<AnnouncementSeverity, React.ReactNode> = {
  info: <InformationCircleIcon className="h-4 w-4" />,
  warning: <ExclamationTriangleIcon className="h-4 w-4" />,
  critical: <BellAlertIcon className="h-4 w-4" />,
};

interface AnnouncementRowProps {
  announcement: Announcement;
  onSelect: (announcement: Announcement) => void;
}

function AnnouncementRow({ announcement, onSelect }: AnnouncementRowProps) {
  const { i18n } = useLingui();
  return (
    <tr
      className="group border-b border-border/50 last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={() => onSelect(announcement)}
    >
      {/* Severity & Title */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <span
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-lg',
              SEVERITY_BADGE_COLORS[announcement.severity]
            )}
          >
            {SEVERITY_ICONS[announcement.severity]}
          </span>
          <div className="min-w-0">
            <span className="font-medium text-foreground block truncate max-w-xs">
              {announcement.title}
            </span>
            {announcement.message && (
              <span className="text-sm text-muted-foreground truncate block max-w-xs">
                {announcement.message}
              </span>
            )}
          </div>
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <Badge
          variant="secondary"
          className={cn('font-medium', STATUS_BADGE_COLORS[announcement.status])}
        >
          {STATUS_LABELS[announcement.status]}
        </Badge>
      </td>

      {/* Severity */}
      <td className="px-4 py-3 hidden sm:table-cell">
        <Badge
          variant="secondary"
          className={cn('font-medium', SEVERITY_BADGE_COLORS[announcement.severity])}
        >
          {SEVERITY_LABELS[announcement.severity]}
        </Badge>
      </td>

      {/* Active Period */}
      <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell">
        {announcement.activeFrom ? (
          <div>
            <span>{formatDate(announcement.activeFrom, i18n.locale)}</span>
            {announcement.activeUntil && (
              <>
                <span className="mx-1">-</span>
                <span>{formatDate(announcement.activeUntil, i18n.locale)}</span>
              </>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground/60">
            <Trans>Not scheduled</Trans>
          </span>
        )}
      </td>

      {/* Created */}
      <td className="px-4 py-3 text-sm text-muted-foreground hidden lg:table-cell">
        {formatDate(announcement.createdAt, i18n.locale)}
      </td>
    </tr>
  );
}

function formatDate(date: Date | string, locale: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

type FilterOption = AnnouncementStatus | 'all';
type SeverityFilterOption = AnnouncementSeverity | 'all';

function getStatusFilterOptions(
  t: ReturnType<typeof useLingui>['t']
): { value: FilterOption; label: string }[] {
  return [
    { value: 'all', label: t({ message: 'All', context: 'filter_option' }) },
    { value: 'active', label: t`Active` },
    { value: 'scheduled', label: t`Scheduled` },
    { value: 'draft', label: t`Draft` },
    { value: 'expired', label: t`Expired` },
  ];
}

function getSeverityFilterOptions(
  t: ReturnType<typeof useLingui>['t']
): { value: SeverityFilterOption; label: string }[] {
  return [
    { value: 'all', label: t`All Severities` },
    { value: 'info', label: t`Info` },
    { value: 'warning', label: t`Warning` },
    { value: 'critical', label: t`Critical` },
  ];
}

export function AdminAnnouncements() {
  const { t, i18n } = useLingui();
  const [statusFilter, setStatusFilter] = useState<FilterOption>('all');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilterOption>('all');
  const [page, setPage] = useState(1);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const limit = 25;
  const STATUS_FILTER_OPTIONS = getStatusFilterOptions(t);
  const SEVERITY_FILTER_OPTIONS = getSeverityFilterOptions(t);

  // Build query params
  const queryParams: AnnouncementQueryParams = useMemo(() => {
    const params: AnnouncementQueryParams = {
      page,
      limit,
    };

    if (statusFilter !== 'all') {
      params.status = statusFilter;
    }

    if (severityFilter !== 'all') {
      params.severity = severityFilter;
    }

    return params;
  }, [page, limit, statusFilter, severityFilter]);

  const { data, isLoading, error } = useAnnouncements(queryParams);

  const handleStatusFilterChange = (filter: FilterOption) => {
    setStatusFilter(filter);
    setPage(1);
  };

  const handleSeverityFilterChange = (filter: SeverityFilterOption) => {
    setSeverityFilter(filter);
    setPage(1);
  };

  const totalPages = data?.pagination.totalPages || 1;
  const total = data?.pagination.total || 0;
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
            <MegaphoneIcon className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold text-foreground">
              <Trans>Announcements</Trans>
            </h1>
            <p className="text-muted-foreground">
              <Trans>Manage system-wide announcements</Trans>
            </p>
          </div>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="rounded-xl gap-2">
          <PlusIcon className="h-4 w-4" />
          <Trans>New Announcement</Trans>
        </Button>
      </div>

      {/* Filters */}
      <Card className="rounded-xl">
        <div className="p-4 space-y-4">
          {/* Status filter pills */}
          <div className="flex flex-wrap gap-2">
            <span className="text-sm font-medium text-muted-foreground self-center mr-2">
              <Trans>Status:</Trans>
            </span>
            {STATUS_FILTER_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handleStatusFilterChange(option.value)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  statusFilter === option.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Severity filter pills */}
          <div className="flex flex-wrap gap-2">
            <span className="text-sm font-medium text-muted-foreground self-center mr-2">
              <Trans>Severity:</Trans>
            </span>
            {SEVERITY_FILTER_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSeverityFilterChange(option.value)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  severityFilter === option.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Announcements table */}
      <Card className="rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-10 w-10 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-4 text-destructive">
              <Trans>Failed to load announcements: {error.message}</Trans>
            </div>
          </div>
        ) : data?.data.length === 0 ? (
          <div className="p-12 text-center">
            <MegaphoneIcon className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground mb-4">
              <Trans>No announcements found</Trans>
            </p>
            <Button
              onClick={() => setShowCreateForm(true)}
              variant="outline"
              className="rounded-xl gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              <Trans>Create your first announcement</Trans>
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      <Trans>Announcement</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      <Trans>Status</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground hidden sm:table-cell">
                      <Trans>Severity</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground hidden md:table-cell">
                      <Trans>Active Period</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground hidden lg:table-cell">
                      <Trans>Created</Trans>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.data.map((announcement) => (
                    <AnnouncementRow
                      key={announcement.id}
                      announcement={announcement}
                      onSelect={setSelectedAnnouncement}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
              <p className="text-sm text-muted-foreground">
                <Trans>
                  Showing {startItem}-{endItem} of {total.toLocaleString(i18n.locale)} announcements
                </Trans>
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-lg"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  <span className="sr-only">
                    <Trans>Previous</Trans>
                  </span>
                </Button>
                <span className="text-sm text-muted-foreground">
                  <Trans>
                    Page {page} of {totalPages}
                  </Trans>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="rounded-lg"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                  <span className="sr-only">
                    <Trans>Next</Trans>
                  </span>
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Create/Edit form sheet */}
      {(showCreateForm || selectedAnnouncement) && (
        <AnnouncementFormSheet
          announcement={selectedAnnouncement}
          onClose={() => {
            setSelectedAnnouncement(null);
            setShowCreateForm(false);
          }}
        />
      )}
    </div>
  );
}
